import { beatTimes, clamp, cleanTimelineReferences, createClip, createTrack, findClip, migrateCreatorProject, snapTime, splitClips, trimClip, updateClip } from './creatorTimeline';

export const CREATOR_PRESETS = {
  reel: { label: 'Reel / TikTok / Short', width: 1080, height: 1920 },
  youtube: { label: 'YouTube HD', width: 1920, height: 1080 },
  portrait: { label: 'Instagram Portrait', width: 1080, height: 1350 },
  square: { label: 'Square', width: 1080, height: 1080 },
};
export const CREATOR_TEMPLATES = [
  { id: 'performance-reel', name: 'Performance Reel', title: 'Mi interpretacion', subtitle: 'Artista / instrumento', preset: 'reel', video: 'Interpretacion', audio: 'Master', blur: true },
  { id: 'guitar-performance', name: 'Guitar Performance', title: 'Guitar Performance', subtitle: 'Guitarra / artista', preset: 'reel', video: 'Guitarra', audio: 'Master', blur: true },
  { id: 'piano-performance', name: 'Piano Performance', title: 'Piano Performance', subtitle: 'Piano / artista', preset: 'reel', video: 'Piano', audio: 'Master', blur: true },
  { id: 'vocal-cover', name: 'Vocal Cover', title: 'Vocal Cover', subtitle: 'Voz / artista', preset: 'reel', video: 'Interpretacion vocal', audio: 'Master', blur: true },
  { id: 'drum-cover', name: 'Drum Cover', title: 'Drum Cover', subtitle: 'Bateria / artista', preset: 'reel', video: 'Bateria', audio: 'Master', blur: true },
  { id: 'music-promo', name: 'Music Promo', title: 'Nuevo lanzamiento', subtitle: 'Artista', preset: 'square', video: 'Promo', audio: 'Cancion' },
  { id: 'photo-reel', name: 'Photo Reel', title: 'Mi historia', subtitle: 'Album', preset: 'reel', image: 'Fotografias', audio: 'Musica' },
  { id: 'youtube-short', name: 'YouTube Short', title: 'Mi Short', subtitle: 'Artista', preset: 'reel', video: 'Short', audio: 'Audio' },
  { id: 'tutorial', name: 'Tutorial', title: 'Mi tutorial', subtitle: 'Leccion', preset: 'youtube', video: 'Demostracion', audio: 'Voz' },
];
export function applyExportPreset(project, id) {
  const preset = CREATOR_PRESETS[id];
  if (!preset) return project;
  return { ...project, canvas: { ...project.canvas, width: preset.width, height: preset.height, fps: 30 },
    settings: { ...project.settings, export_preset: id } };
}
export function applyCreatorTemplate(project, id) {
  const template = CREATOR_TEMPLATES.find(item => item.id === id);
  if (!template) return project;
  const base = applyExportPreset(migrateCreatorProject(project), template.preset);
  const title = createClip(null, 0);
  title.duration = 4; title.text.content = template.title; title.text.bold = true; title.transform.y = .17;
  const subtitle = createClip(null, 0);
  subtitle.duration = 4; subtitle.text.content = template.subtitle; subtitle.text.size = 36; subtitle.transform.y = .24;
  const ending = createClip(null, 26);
  ending.duration = 4; ending.text.content = 'Gracias por escuchar'; ending.text.size = 44; ending.fade_in = .4;
  return { ...base, title: template.name, beat_grid: null, markers: [],
    canvas: { ...base.canvas, background_mode: template.blur ? 'blur_video' : 'color' },
    settings: { ...base.settings, template_id: id },
    tracks: [createTrack(template.image ? 'image' : 'video', template.image || template.video),
      { ...createTrack('audio', template.audio), role: template.id === 'tutorial' ? 'voice' : 'music' },
      createTrack('image', 'Logo'), createTrack('text', 'Titulo', [title, subtitle]), createTrack('text', 'Cierre', [ending])] };
}

export function previousTransitionClip(project, id) {
  const found = findClip(project, id);
  if (!found) return null;
  const clips = [...found.track.clips].sort((a, b) => a.start - b.start);
  return clips[clips.findIndex(clip => clip.id === id) - 1] || null;
}
export function applyTransition(project, id, type, requestedDuration = .5, options = {}) {
  if (!['cut', 'fade', 'dissolve'].includes(type)) return project;
  const found = findClip(project, id);
  const previous = previousTransitionClip(project, id);
  if (!found || found.track.locked || !previous || !Number.isFinite(requestedDuration)) return project;
  let next = project;
  if (project.settings.snap_to_beat && options.snap !== false) {
    const edge = previous.start + previous.duration;
    const snapped = snapTime(edge, project, { exclude: [previous.id, id] });
    if (snapped !== edge) next = trimClip(project, previous.id, 'right', snapped, { snap: false });
  }
  const source = findClip(next, previous.id).clip;
  const maximum = Math.min(source.duration, found.clip.duration) / 2;
  const duration = type === 'cut' ? 0 : Math.round(clamp(requestedDuration, Math.min(1 / project.canvas.fps, maximum), maximum) * 1000000) / 1000000;
  const start = Math.round((source.start + source.duration - (type === 'dissolve' ? duration : 0)) * 1000000) / 1000000;
  return updateClip(next, id, clip => ({ ...clip, start, transition_in: { type, duration, from_clip_id: previous.id } }));
}
export function autoCutToBeats(project, ids, every = 4) {
  if (![1, 2, 4].includes(every)) return project;
  const cuts = beatTimes(project).filter((_, index) => index % every === 0);
  const tracks = new Set(project.tracks.filter(track => track.clips.some(clip => ids.includes(clip.id))).map(track => track.id));
  const spans = ids.map(id => findClip(project, id)).filter(item => item && !item.track.locked)
    .map(item => ({ track: item.track.id, start: item.clip.start, end: item.clip.start + item.clip.duration }));
  return cuts.reduce((next, time) => {
    const selected = next.tracks.filter(track => tracks.has(track.id)).flatMap(track => track.clips.filter(clip => spans.some(span => span.track === track.id && clip.start >= span.start && clip.start + clip.duration <= span.end + .000001)).map(clip => clip.id));
    return splitClips(next, selected, time, { snap: false });
  }, project);
}

const captionTime = value => {
  const match = /^(?:(\d{1,2}):)?(\d{2}):(\d{2})[.,](\d{3})$/.exec(value.trim());
  if (!match || Number(match[2]) >= 60 || Number(match[3]) >= 60) throw new Error('Tiempo de caption invalido.');
  return Number(match[1] || 0) * 3600 + Number(match[2]) * 60 + Number(match[3]) + Number(match[4]) / 1000;
};
const plainCaption = value => value.replace(/<(script|style)\b[^>]*>[\s\S]*?<\/\1>/gi, '').replace(/<[^>]*>/g, '')
  .replace(/&(?:amp|lt|gt|quot|apos|nbsp);/g, entity => ({ '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&apos;': "'", '&nbsp;': ' ' })[entity]).trim();
export function parseCreatorCaptions(source, { available = 80, maxSeconds = 1800 } = {}) {
  if (typeof source !== 'string' || new TextEncoder().encode(source).length > 256 * 1024) throw new Error('El archivo de captions supera 256 KiB.');
  const normalized = source.replace(/^\uFEFF/, '').replace(/\r\n?/g, '\n').trim();
  const blocks = normalized.split(/\n[ \t]*\n/);
  const cues = [];
  for (const block of blocks) {
    const lines = block.split('\n');
    if (/^(WEBVTT|NOTE(?:\s|$))/.test(lines[0])) continue;
    if (/^(STYLE|REGION)(?:\s|$)/.test(lines[0])) throw new Error('Styles y regiones VTT no admitidos.');
    const index = lines.findIndex(line => line.includes('-->'));
    if (index < 0 || index > 1) throw new Error('Formato SRT/VTT invalido.');
    const match = /^(\S+)\s+-->\s+(\S+)(?:\s+[^\n]*)?$/.exec(lines[index]);
    if (!match) throw new Error('Intervalo de caption invalido.');
    const start = captionTime(match[1]), end = captionTime(match[2]);
    const text = plainCaption(lines.slice(index + 1).join('\n'));
    if (end <= start || end > maxSeconds || !text || text.length > 2000) throw new Error('Caption vacio, demasiado largo o fuera del limite temporal.');
    cues.push({ start, end, text });
    if (cues.length > available) throw new Error('No hay espacio para mas clips de captions (limite 80).');
  }
  if (!cues.length) throw new Error('No se encontraron captions.');
  return cues;
}
export function importCreatorCaptions(project, source) {
  const cues = parseCreatorCaptions(source, { available: 80 - project.tracks.flatMap(track => track.clips).length });
  const clips = cues.map(cue => {
    const clip = createClip(null, cue.start);
    return { ...clip, caption: true, duration: Math.round((cue.end - cue.start) * 1000000) / 1000000,
      transform: { ...clip.transform, y: .85 }, text: { ...clip.text, content: cue.text, size: 42, background: '#000000cc' } };
  });
  const track = project.tracks.find(item => item.type === 'text' && !item.locked && item.name === 'Captions');
  if (!track && project.tracks.length >= 16) throw new Error('Limite de 16 pistas alcanzado.');
  return { ...project, tracks: track ? project.tracks.map(item => item.id === track.id ? { ...item, clips: [...item.clips, ...clips] } : item)
    : [...project.tracks, createTrack('text', 'Captions', clips)] };
}
const subtitleTimestamp = (seconds, format) => {
  const ms = Math.max(0, Math.round(seconds * 1000));
  return `${String(Math.floor(ms / 3600000)).padStart(2, '0')}:${String(Math.floor(ms / 60000) % 60).padStart(2, '0')}:${String(Math.floor(ms / 1000) % 60).padStart(2, '0')}${format === 'srt' ? ',' : '.'}${String(ms % 1000).padStart(3, '0')}`;
};
export function exportCreatorCaptions(project, format = 'srt') {
  if (!['srt', 'vtt'].includes(format)) throw new Error('Formato de captions invalido.');
  const clips = project.tracks.filter(track => !track.hidden).flatMap(track => track.clips.filter(clip => clip.caption && clip.text))
    .sort((a, b) => a.start - b.start || a.id.localeCompare(b.id));
  return (format === 'vtt' ? 'WEBVTT\n\n' : '') + clips.map((clip, index) => `${index + 1}\n${subtitleTimestamp(clip.start, format)} --> ${subtitleTimestamp(clip.start + clip.duration, format)}\n${clip.text.content.replace(/-->/g, '--\u003e').replace(/\n\s*\n/g, '\n')}`).join('\n\n') + (clips.length ? '\n' : '');
}
export function replacePitchAsset(project, clipId, derived) {
  const found = findClip(project, clipId);
  if (!found?.asset || found.track.locked || derived.kind !== 'audio') return project;
  const ratio = found.asset.duration / derived.duration;
  if (!Number.isFinite(ratio) || ratio <= 0) return project;
  return updateClip(project, clipId, clip => {
    const duration = Math.min(clip.duration / ratio, (derived.duration - clip.source_start / ratio) / clip.speed);
    return { ...clip, asset_id: derived.id, source_start: clip.source_start / ratio, duration,
      fade_in: clip.fade_in / ratio, fade_out: clip.fade_out / ratio, transition_in: null };
  });
}
export function addAlignedAudio(project, clipId, assets) {
  const found = findClip(project, clipId);
  if (!found?.asset || found.track.locked) return project;
  const selected = assets.filter(asset => asset.kind === 'audio' && !project.tracks.some(track => track.clips.some(clip => clip.asset_id === asset.id && clip.start === found.clip.start)));
  if (!selected.length) return project;
  if (project.tracks.length + selected.length > 16 || project.tracks.flatMap(track => track.clips).length + selected.length > 80) throw new Error('No hay espacio para las pistas derivadas.');
  const tracks = selected.map(asset => ({ ...createTrack('audio', asset.filename.slice(0, 120), [{ ...createClip(asset, found.clip.start),
    source_start: found.clip.source_start, speed: found.clip.speed,
    duration: Math.min(found.clip.duration, (asset.duration - found.clip.source_start) / found.clip.speed) }]),
    role: /vocal/i.test(asset.derivation?.parameters?.stem || asset.filename) ? 'voice' : 'music' }));
  if (tracks.some(track => track.clips[0].duration <= 0)) throw new Error('El audio derivado no cubre el intervalo del clip.');
  const muted = updateClip(project, clipId, { muted: true });
  return cleanTimelineReferences({ ...muted, tracks: [...muted.tracks, ...tracks] });
}
